import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarDays, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdateProfile } from '@/api/hooks/useAccount';
import { useVenueContext } from '@/providers/VenueProvider';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

/**
 * Customer-facing navbar for the venue detail page and booking flow.
 *
 * <p>Distinct from {@link /components/marketing/MarketingNavbar} — that one
 * targets operators evaluating the platform and exposes a "Book a demo"
 * CTA. A customer arriving from an operator's "Book Now" link to book a
 * session shouldn't see "Book a demo" as the primary action.
 *
 * <p>Solid white background (no transparent-over-hero trick) so it works
 * against the venue page's light body. "Book Now" uses the current slug
 * from {@link VenueProvider} so the CTA keeps routing to the right venue
 * even after the customer navigates outside the venue subtree.
 */
export default function VenueNavbar() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const updateProfile = useUpdateProfile();
  const { currentSlug } = useVenueContext();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const bookHref = `/venue/${currentSlug}/book`;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleLocale = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    // Same fire-and-forget profile write as CustomerLayout — keeps the
    // single-source-of-truth model consistent across the two customer
    // header surfaces. See doc/DECISIONS.md "Language Switcher Single
    // Source of Truth".
    if (isAuthenticated) {
      updateProfile.mutate(
        { locale: newLang },
        {
          onError: (err) => {
            console.warn('Failed to persist language preference to profile', err);
          },
        },
      );
    }
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link
          to="/"
          className="text-lg font-semibold tracking-tight text-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 rounded-sm"
        >
          RekPlay
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-3 md:flex">
          <Link
            to={bookHref}
            className="group inline-flex items-center gap-1.5 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
          >
            Book Now
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="min-w-[36px] text-neutral-700"
            onClick={toggleLocale}
          >
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </Button>
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#C2713A] text-xs text-white">
                    {initials || 'U'}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/bookings')}>
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {t('booking.my_bookings', 'My bookings')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/credits')}>
                  {t('credit.credits_title', 'Credits')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.logout', 'Log out')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className={
                buttonVariants({ variant: 'ghost', size: 'sm' }) +
                ' text-neutral-700 focus-visible:ring-[#C2713A]'
              }
            >
              {t('auth.login', 'Log in')}
            </Link>
          )}
        </nav>

        {/* Mobile — Book Now always visible, everything else under the hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          <Link
            to={bookHref}
            className="inline-flex items-center gap-1 rounded-md bg-[#C2713A] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#a95f2f]"
          >
            Book Now
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            className="rounded-md p-2 text-neutral-700 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-neutral-200 bg-white px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-2">
            <Button variant="ghost" size="sm" onClick={toggleLocale}>
              {i18n.language === 'en' ? 'Français' : 'English'}
            </Button>
            {isAuthenticated ? (
              <>
                <Link
                  to="/bookings"
                  className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                  onClick={() => setMobileOpen(false)}
                >
                  {t('booking.my_bookings', 'My bookings')}
                </Link>
                <Link
                  to="/credits"
                  className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                  onClick={() => setMobileOpen(false)}
                >
                  {t('credit.credits_title', 'Credits')}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                >
                  {t('auth.logout', 'Log out')}
                </Button>
              </>
            ) : (
              <Link
                to="/login"
                className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                onClick={() => setMobileOpen(false)}
              >
                {t('auth.login', 'Log in')}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
